import { apiClient, ApiClient } from "@/lib/api/api-client";
import { BaseResponse } from "@/modules/types/response";
import {
  ApplicantStatus,
  JobType,
  MoneyUnit,
  PostType,
  SortByJobField,
  SortType,
} from "@/modules/types/enum";
import { Job, JobSuggestion } from "@/modules/types/job/job";
import { CandidateAppliedJob } from "../types/job/jobApply";
import { Applicant } from "../types/conversation/conversation";

interface JobResp extends BaseResponse {
  jobs?: Job[];
  total: number;
  nextId?: string;
}

interface JobSearchResp extends BaseResponse {
  jobSuggests?: JobSuggestion[];
}

interface JobDetailResp extends BaseResponse {
  job?: Job;
}

interface ApplyJobResp extends BaseResponse {
  jobApplyId: string;
}

interface SavedJobResp extends BaseResponse {
  jobs: Job[];
  total: number;
  nextId?: string;
}

interface JobApplyResp extends BaseResponse {
  jobs: CandidateAppliedJob[];
  total: number;
}

interface TotalAllJobResp extends BaseResponse {
  totalApplied: number;
  totalSaved: number;
  totalOffer: number;
  totalHired: number;
  totalRejected: number;
}

/** GET /report/candidate — counts for the authenticated candidate (Bearer token). */
export interface CandidateReportResp extends BaseResponse {
  totalApplies: number;
  totalSaved: number;
  totalOffer: number;
}

export interface ApplicantByIdResponse extends BaseResponse {
  jobApply: Applicant;
}

export class JobService {
  private BASE_PATH =
    process.env.API_JOB_ENDPOINT || "http://localhost:8081/job";
  private apiClient: ApiClient = apiClient;
  private apiVersion = "v1.0.0";
  constructor(apiVersion?: string) {
    this.apiVersion = apiVersion || this.apiVersion;
  }

  async searchSuggestion(keyword: string): Promise<JobSearchResp> {
    return this.apiClient.fetch<JobSearchResp>(
      `${this.BASE_PATH}/${this.apiVersion}/search${
        keyword && keyword.length > 0 ? `?keyword=${keyword}` : ""
      }`
    );
  }

  async getDetailBySlug(slug: string): Promise<JobDetailResp> {
    return this.apiClient.fetch<JobDetailResp>(
      `${this.BASE_PATH}/${this.apiVersion}/slug/${slug}`
    );
  }

  async getListJobByBusinessSlug(slug: string): Promise<JobResp> {
    return this.apiClient.fetch<JobResp>(
      `${this.BASE_PATH}/${this.apiVersion}/company/${slug}`
    );
  }

  async getList({
    keyword,
    categoryIds,
    levelIds,
    degreeIds,
    languageIds,
    skillIds,
    skillName,
    jobTypes,
    longitude,
    latitude,
    distance,
    country,
    city,
    province,
    district,
    ward,
    limit,
    page,
    nextId,
    location,
    minSalary,
    maxSalary,
    moneyUnit,
    minExperience,
    maxExperience,
    ignoreIds,
    includeIds,
    postTypes,
    sortBy,
    excludeJobIds,
  }: {
    keyword?: string;
    categoryIds?: string[];
    levelIds?: string[];
    degreeIds?: string[];
    languageIds?: string[];
    skillIds?: string[];
    skillName?: string;
    jobTypes?: JobType[];
    longitude?: number;
    latitude?: number;
    distance?: number;
    country?: string;
    city?: string;
    province?: string;
    district?: string;
    ward?: string;
    limit?: number;
    page?: number;
    nextId?: string;
    location?: string;
    minSalary?: number;
    maxSalary?: number;
    moneyUnit?: MoneyUnit;
    minExperience?: number;
    maxExperience?: number;
    ignoreIds?: string[];
    includeIds?: string[];
    postTypes?: PostType[];
    sortBy?: SortByJobField;
    excludeJobIds?: string[];
  }): Promise<JobResp | undefined> {
    const queryParam = new URLSearchParams();
    if (keyword) queryParam.append("keyword", keyword);
    if (categoryIds?.length)
      categoryIds.forEach((categoryId) =>
        queryParam.append("categoryIds", categoryId)
      );
    if (levelIds?.length)
      levelIds.forEach((levelId) => queryParam.append("levelIds", levelId));
    if (degreeIds?.length)
      degreeIds.forEach((degreeId) => queryParam.append("degreeIds", degreeId));
    if (languageIds?.length)
      languageIds.forEach((languageId) =>
        queryParam.append("languageIds", languageId)
      );
    if (skillIds?.length)
      skillIds.forEach((skillId) => queryParam.append("skillIds", skillId));
    if (skillName) queryParam.append("skillName", skillName);
    if (jobTypes?.length)
      jobTypes.forEach((jobType) => queryParam.append("jobTypes", jobType));
    if (longitude !== undefined)
      queryParam.append("longitude", longitude.toString());
    if (latitude !== undefined)
      queryParam.append("latitude", latitude.toString());
    if (distance !== undefined)
      queryParam.append("distance", distance.toString());
    if (country) queryParam.append("country", country);
    if (city) queryParam.append("city", city);
    if (province) queryParam.append("province", province);
    if (district) queryParam.append("district", district);
    if (ward) queryParam.append("ward", ward);
    if (limit !== undefined) queryParam.append("limit", limit.toString());
    if (page !== undefined) queryParam.append("page", page.toString());
    if (nextId) queryParam.append("nextId", nextId);
    if (location) queryParam.append("location", location);
    if (postTypes?.length)
      postTypes.forEach((postType) => queryParam.append("postTypes", postType));
    if (sortBy) queryParam.append("sortBy", sortBy);
    if (minSalary) queryParam.append("minSalary", minSalary.toString());
    if (maxSalary) queryParam.append("maxSalary", maxSalary.toString());
    if (moneyUnit) queryParam.append("moneyUnit", moneyUnit);
    if (minExperience)
      queryParam.append("minExperience", minExperience.toString());
    if (maxExperience)
      queryParam.append("maxExperience", maxExperience.toString());
    if (ignoreIds?.length)
      ignoreIds.forEach((ignoreId) => queryParam.append("ignoreIds", ignoreId));
    if (includeIds?.length)
      includeIds.forEach((includeId) =>
        queryParam.append("includeIds", includeId)
      );
    if (excludeJobIds?.length)
      excludeJobIds.forEach((excludeJobId) =>
        queryParam.append("ignoreIds", excludeJobId)
      );

    const queryString = queryParam.toString();
    return this.apiClient.fetch<JobResp>(
      `${this.BASE_PATH}/${this.apiVersion}/list${
        queryString ? `?${queryString}` : ""
      }`
    );
  }

  async viewJob(jobId: string): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/view/id/${jobId}`,
      {
        method: "GET",
      }
    );
  }

  async applyJob(
    jobId: string,
    employeeId: string,
    coverLetter: string,
    minDesiredSalary: number,
    maxDesiredSalary: number,
    moneyUnit: MoneyUnit
  ): Promise<ApplyJobResp> {
    console.log(moneyUnit);
    return this.apiClient.fetch<ApplyJobResp>(
      `${this.BASE_PATH}/${this.apiVersion}/apply/${jobId}`,
      {
        method: "POST",
        body: JSON.stringify({
          candidateId: employeeId,
          coverLetter,
          minDesiredSalary,
          maxDesiredSalary,
          // moneyUnit,
        }),
      }
    );
  }

  async saveJob(jobId: string, employeeId: string): Promise<BaseResponse> {
    return this.apiClient.fetch<ApplyJobResp>(
      `${this.BASE_PATH}/${this.apiVersion}/save`,
      {
        method: "POST",
        body: JSON.stringify({
          jobId,
          employeeId,
        }),
      }
    );
  }

  async unSaveJob(jobId: string, employeeId: string): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/unsave`,
      {
        method: "POST",
        body: JSON.stringify({
          jobId,
          employeeId,
        }),
      }
    );
  }

  async getSavedJobs(employeeId: string): Promise<SavedJobResp> {
    return this.apiClient.fetch<SavedJobResp>(
      `${this.BASE_PATH}/${this.apiVersion}/saved-jobs/${employeeId}`
    );
  }

  async getJobApplies(
    employeeId: string,
    page: number,
    limit: number,
    jobTitle?: string,
    jobId?: string,
    status?: ApplicantStatus,
    sortBy?: SortByJobField,
    sortType?: SortType,
    applyId?: string
  ): Promise<JobApplyResp> {
    const queryParam = new URLSearchParams();
    if (jobTitle) queryParam.append("jobTitle", jobTitle);
    if (jobId) queryParam.append("jobId", jobId);
    if (status) queryParam.append("status", status);
    if (sortBy) queryParam.append("sortBy", sortBy);
    if (sortType) queryParam.append("sortType", sortType);
    if (page) queryParam.append("page", page.toString());
    if (limit) queryParam.append("limit", limit.toString());
    if (applyId) queryParam.append("applyId", applyId);
    return this.apiClient.fetch<JobApplyResp>(
      `${this.BASE_PATH}/${
        this.apiVersion
      }/job-applied/candidate/${employeeId}${
        queryParam.toString() ? `?${queryParam.toString()}` : ""
      }`
    );
  }

  async getApplicantById(applicantId: string): Promise<ApplicantByIdResponse> {
    return await this.apiClient.fetch<ApplicantByIdResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/apply/${applicantId}`
    );
  }

  async getById(jobId: string): Promise<JobDetailResp> {
    return this.apiClient.fetch<JobDetailResp>(
      `${this.BASE_PATH}/${this.apiVersion}/id/${jobId}`
    );
  }

  async getTotalAllJob(employeeId: string): Promise<TotalAllJobResp> {
    return this.apiClient.fetch<TotalAllJobResp>(
      `${this.BASE_PATH}/${this.apiVersion}/job-total${
        employeeId ? `?employeeId=${employeeId}` : ""
      }`
    );
  }

  async getCandidateReport(): Promise<CandidateReportResp> {
    return this.apiClient.fetch<CandidateReportResp>(
      `${this.BASE_PATH}/${this.apiVersion}/report/candidate`
    );
  }

  async acceptExchange(
    jobApplyId: string,
    candidateId: string
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/apply/${jobApplyId}/candidate-exchange?candidateId=${candidateId}`,
      {
        method: "POST",
      }
    );
  }

  async cancelExchange(
    jobApplyId: string,
    candidateId: string
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/apply/${jobApplyId}/candidate-cancel?candidateId=${candidateId}`,
      {
        method: "POST",
      }
    );
  }

  async denyOffer(
    jobApplyId: string,
    candidateId: string
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/apply/${jobApplyId}/candidate-denied?candidateId=${candidateId}`,
      {
        method: "POST",
      }
    );
  }

  async acceptOffer(
    jobApplyId: string,
    candidateId: string
  ): Promise<BaseResponse> {
    return this.apiClient.fetch<BaseResponse>(
      `${this.BASE_PATH}/${this.apiVersion}/apply/${jobApplyId}/candidate-hired?candidateId=${candidateId}`,
      {
        method: "POST",
      }
    );
  }
}

export const jobService = new JobService();
